import React, { useState } from 'react'
import {
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Grow,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Select,
  MenuItem,
} from '@mui/material'
import Address from 'components/Address'
import SelectTimeZone from 'components/SelectTimeZone'
import CountrySelector from 'components/CountrySelector'
import ResponsiveDialog from 'components/ResponsiveDialog'
import FocusFadeGroup from 'components/FocusFadeGroup'
import MoreButtonCard from 'components/MoreButtonCard'
import TimezoneRangeField from 'components/TimezoneRangeField'
import { LocationData } from 'services/location'
import { FormValue } from 'types'
import { Create, Help, LocationOn } from '@mui/icons-material'
import { TransitionProps } from '@mui/material/transitions'
import FormErrorHint from '../../FormErrorHint'
import useStyles from './styles'

const locationTypeOptions = [
  { label: 'Specific location', value: 'full_address' },
  { label: 'Specific timezone', value: 'job_timezone' },
  { label: 'Specific countries', value: 'specific_countries' },
  { label: 'Anywhere', value: 'anywhere' },
]
const TransitionComponent = React.forwardRef<HTMLDivElement, TransitionProps>(({ children, ...props }, ref) => (
  <Grow ref={ref} {...props}>
    {children as React.ReactElement}
  </Grow>
))

export interface IJobLocationFieldsProps {
  editable: boolean
  location_type: FormValue<string>
  job_countries: FormValue<{ code: string, name: string }[]>
  job_timezone: FormValue<number>
  timezone_value: FormValue<number>
  timezone_range: FormValue<number>
  full_address: FormValue<string>
  default_distance: FormValue<number>
  country: FormValue<string>
  region: FormValue<string>
  city: FormValue<string>
  location_latitude: FormValue<number>
  location_longitude: FormValue<number>
}

export interface IJobLocationFieldsState {
  isDialogOpen: boolean
}

function JobLocationFields(props: IJobLocationFieldsProps) {
  const { classes } = useStyles()
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const handleDialogClose = () => {
    setIsDialogOpen(false)
  }

  const handleDialogOpen = () => {
    setIsDialogOpen(true)
  }

  const handleFullAddressChange = (e: React.SyntheticEvent<any>, address: LocationData) => {
    const { full_address, region, country, city, location_latitude, location_longitude } = props

    e.preventDefault()

    full_address.input.onChange(address.placeName)
    region.input.onChange(address.region)
    country.input.onChange(address.country)
    city.input.onChange(address.city)
    location_latitude.input.onChange(address.latitude)
    location_longitude.input.onChange(address.longitude)
  }

  const renderDialog = () => {
    const { location_type, default_distance, full_address, job_timezone, timezone_range, job_countries } = props

    if (isDialogOpen) {
      const selectedLocationType = location_type.input.value
      // const containerBounds = this.container.getBoundingClientRect()

      return (
        <ResponsiveDialog
          open
          onClose={handleDialogClose}
          TransitionComponent={TransitionComponent as any}
          // TransitionProps={{
          //   style: {
          //     transformOrigin: `${containerBounds.left}px ${containerBounds.top}px`,
          //   },
          // }}
        >
          <DialogTitle>
            Edit job location
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Select
                  style={{ width: 9999, maxWidth: '100%' }}
                  value={location_type.input.value || 'anywhere'}
                  onChange={location_type.input.onChange}
                  onBlur={location_type.input.onBlur}
                >
                  {locationTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {selectedLocationType === 'full_address' && (
                <React.Fragment>
                  <Grid item xs={12}>
                    <Address
                      value={full_address.input.value}
                      onChange={full_address.input.onChange}
                      onSelectLocation={handleFullAddressChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Max miles from location for candidates"
                      value={default_distance.input.value}
                      onChange={event => default_distance.input.onChange(parseFloat(event.target.value || '0'))}
                      onBlur={default_distance.input.onBlur}
                    />
                  </Grid>
                </React.Fragment>
              )}

              {selectedLocationType === 'specific_countries' && (
                <Grid item xs={12}>
                  <CountrySelector
                    input={job_countries.input}
                    meta={job_countries.meta}
                    label="Countries"
                    placeholder="Start typing countries..."
                    fullWidth
                  />
                </Grid>
              )}

              {selectedLocationType === 'job_timezone' && (
                <React.Fragment>
                  <Grid item xs={12} md={6}>
                    <SelectTimeZone
                      fullWidth
                      input={job_timezone.input}
                      meta={job_timezone.meta}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TimezoneRangeField
                      fullWidth
                      input={timezone_range.input}
                      meta={timezone_range.meta}
                    />
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleDialogClose}>
              Ok
            </Button>
          </DialogActions>
        </ResponsiveDialog>
      )
    }

    return null
  }

  const renderSecondaryText = (secondaryText: React.ReactNode) => {
    const { location_type, job_countries, job_timezone, full_address, country, region, city } = props

    return (
      <React.Fragment>
        {secondaryText}

        <FormErrorHint fieldMeta={location_type.meta} />
        <FormErrorHint fieldMeta={job_countries.meta} />
        <FormErrorHint fieldMeta={job_timezone.meta} />
        <FormErrorHint fieldMeta={full_address.meta} />
        <FormErrorHint fieldMeta={country.meta} />
        <FormErrorHint fieldMeta={region.meta} />
        <FormErrorHint fieldMeta={city.meta} />
      </React.Fragment>
    )
  }

  const renderEditButton = () => {
    const { editable } = props

    if (editable) {
      return (
        <ListItemIcon className={classes.editButton}>
          <Tooltip title="Edit job location" placement="right">
            <IconButton onClick={handleDialogOpen}>
              <Create />
            </IconButton>
          </Tooltip>
        </ListItemIcon>
      )
    }

    return null
  }

  const renderTimezoneListItemText = () => {
    const { timezone_value, timezone_range, job_timezone, editable } = props
    const timezoneValue = timezone_value.input.value
    const timezoneRange = timezone_range.input.value

    if (timezoneRange > 0) {
      const timezoneStart = timezoneValue - timezoneRange
      const timezoneEnd = timezoneValue + timezoneRange

      return (
        <ListItemText
          className={classes.listItemText}
          primary={(
            <div className={classes.primaryText}>
              GMT{timezoneStart > 0 ? '+' : ''}{timezoneStart !== 0 && timezoneStart} to GMT{timezoneEnd > 0 ? '+' : ''}{timezoneEnd !== 0 && timezoneEnd}
            </div>
          )}
          secondary={renderSecondaryText('Timezone')}
        />
      )
    }

    return (
      <ListItemText
        className={classes.listItemText}
        primary={(
          <div className={classes.primaryText}>
            {!editable && (
              <React.Fragment>
                {job_timezone.input.value}
              </React.Fragment>
            )}

            {editable && (
              <React.Fragment>
                GMT{timezoneValue > 0 ? '+' : ''}{timezoneValue !== 0 && timezoneValue}
              </React.Fragment>
            )}
          </div>
        )}
        secondary={renderSecondaryText('Timezone')}
      />
    )
  }

  const renderListItem = () => {
    const { location_type, job_countries, full_address, country, region, city, editable } = props

    if (location_type.input.value === 'anywhere' || !location_type.input.value) {
      return (
        <ListItem className={classes.item}>
          <ListItemIcon className={classes.icon}>
            <LocationOn />
          </ListItemIcon>

          <ListItemText
            className={classes.listItemText}
            primary={(
              <div className={classes.primaryText}>
                Remote/anywhere
              </div>
            )}
            secondary={renderSecondaryText('Location')}
          />

          {renderEditButton()}
        </ListItem>
      )
    }

    if (location_type.input.value === 'specific_countries') {
      let countriesString = (job_countries.input.value || []).map(c => c.name).join(', ') || ''
      let primaryText: React.ReactNode = countriesString

      if (countriesString.length > 40) {
        primaryText = (
          <React.Fragment>
            {(job_countries.input.value || []).length} Countries

            {!editable && (
              <MoreButtonCard icon={<Help />} className={classes.helpButton}>
                <div className={classes.countriesWrapper}>
                  {countriesString}
                </div>
              </MoreButtonCard>
            )}
          </React.Fragment>
        )
      }

      return (
        <ListItem className={classes.item}>
          <ListItemIcon className={classes.icon} title={countriesString}>
            <LocationOn />
          </ListItemIcon>

          <ListItemText
            className={classes.listItemText}
            primary={(
              <div className={classes.primaryText} title={countriesString}>
                {primaryText || (<span style={{ opacity: 0.6 }}>None</span>)}
              </div>
            )}
            secondary={renderSecondaryText('Countries')}
          />

          {renderEditButton()}
        </ListItem>
      )
    }

    if (location_type.input.value === 'job_timezone') {
      return (
        <ListItem className={classes.item}>
          <ListItemIcon className={classes.icon}>
            <LocationOn />
          </ListItemIcon>

          {renderTimezoneListItemText()}
          {renderEditButton()}
        </ListItem>
      )
    }

    if (location_type.input.value === 'full_address') {
      let jobAddress

      if (full_address.input.value) {
        jobAddress = full_address.input.value
      } else {
        const locationParts = [
          country.input.value,
          region.input.value,
          city.input.value,
        ].filter(part => !!part)

        if (locationParts.length > 0) {
          jobAddress = locationParts.join(', ')
        }
      }

      return (
        <ListItem className={classes.item}>
          <ListItemIcon className={classes.icon}>
            <LocationOn />
          </ListItemIcon>

          <ListItemText
            className={classes.listItemText}
            primary={(
              <div className={classes.primaryText}>
                {jobAddress || 'No address specified'}
              </div>
            )}
            secondary={renderSecondaryText('Location')}
          />

          {renderEditButton()}
        </ListItem>
      )
    }

    return null
  }

  return (
    <React.Fragment>
      <Grid item xs={12} md={3}>
        <List disablePadding dense>
          <FocusFadeGroup focused={false}>
            {renderListItem()}
          </FocusFadeGroup>
        </List>
      </Grid>

      {renderDialog()}
    </React.Fragment>
  )
}

export default JobLocationFields
